import React from 'react';
import { TFunction } from 'i18next';
import { MemberRowAction, MemberRowMode } from './MemberRowTypes';
import { TableCell, TableRow } from '../../organisms/Table';
import { DateFormat } from '../../../_lib/components/DateFormat';
import { ProfilePictureModifier, ProfilePictureName } from '../../../_lib/components/ProfilePictureName';
import { TableCellRole } from '../../organisms/Table/partials/TableCellRole';
import { MemberType } from '../../../types/api/MemberType';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { MemberDeleteForm } from './partials/MemberDeleteForm';
import { RoleChangeForm } from './partials/RoleChangeForm';
import { MemberActions } from './partials/MemberActions';
import { useMemberRow } from './useMemberRow';
import { ButtonRefType } from '../Btn/Btn';
import ModalDialog from '../../organisms/ModalDialog/ModalDialog';

type MemberRowType = {
  member: MemberType;
  hideStatus?: boolean;
  mode?: MemberRowMode | null;
  actionsDisabled?: boolean;

  // Events
  onChangeMode: (mode: MemberRowMode | null) => void;
  onRequestFinished: () => void;
};

/**
 * Renders a Tablerow from an specific User/Member
 * [Sebastian Faller] revise component for better decoupling
 * to child and parent components, because at the moment we need a lot of deep
 * copy of objects to avoid unwanted behaviour due to refernces that where passed around.
 */
export function MemberRow({ member, hideStatus = false, mode = null, onChangeMode, onRequestFinished }: MemberRowType) {
  const { t } = useSonTranslation('MemberRow');
  const labels = {
    nameLabel: t('labels.name'),
    roleLabel: t('labels.role'),
    joinDateLabel: t('labels.joinDate'),
    statusLabel: t('labels.status'),
    functionsLabel: t('labels.functions'),
  };

  const { editButtonRef, removeButtonRef, changeOrToggleMode } = useMemberRow(
    onChangeMode,
    onRequestFinished,
    t as TFunction,
    mode,
  );

  const memberHasActions = Object.keys(member.actions).length > 0;
  const mappedToNewRoleStatusStuff = member.rolesWithStatus.map(r => ({
    name: r.role,
    automaticallyAssigned: r.automaticallyAssigned,
  }));

  return (
    <>
      <TableRow key={member.id}>
        <TableCell dataHead={labels.nameLabel}>
          <ProfilePictureName {...member} modifier={ProfilePictureModifier.ALIGN_TOP} />
        </TableCell>
        <TableCellRole label={labels.roleLabel} roles={mappedToNewRoleStatusStuff} />
        <TableCell dataHead={labels.joinDateLabel}>
          <DateFormat date={member.joinDate} />
        </TableCell>
        {!hideStatus && (
          <TableCell dataHead={labels.statusLabel}>
            {member.rolesWithStatus.map(role => t(`status.${role.status}` as any))[0]}
          </TableCell>
        )}
        <TableCell dataHead={labels.functionsLabel}>
          {memberHasActions && (
            <div className="member-row">
              <MemberActions
                mode={mode}
                member={member}
                editButtonRef={editButtonRef as ButtonRefType}
                removeButtonRef={removeButtonRef as ButtonRefType}
                changeOrToggleMode={changeOrToggleMode}
                t={t as TFunction}
              />
            </div>
          )}
        </TableCell>
      </TableRow>
      {mode === MemberRowMode.removeUser && (
        <ModalDialog
          closeModal={() => changeOrToggleMode(MemberRowMode.default)}
          headline={t(`status.${MemberRowAction.remove}.headlineModal`, { name: member.profileName })}
          initialFocusIndex={1}
          focusOnReturn={editButtonRef}
        >
          <MemberDeleteForm member={member} form={member.actions.remove} />
        </ModalDialog>
      )}
      {mode === MemberRowMode.editRoles && (
        <ModalDialog
          closeModal={() => changeOrToggleMode(MemberRowMode.default)}
          headline={t(`status.${MemberRowAction.editRoles}.headlineModal`, { name: member.profileName })}
          initialFocusIndex={3}
          focusOnReturn={editButtonRef}
        >
          <RoleChangeForm form={member.actions.editRoles} member={member} />
        </ModalDialog>
      )}
    </>
  );
}
